/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import Player from '../models/Player';
import timeToPercentage from '../utils/timeToPercentage';
import percentageToTime from '../utils/percentageToTime';
import htmlMediaEvents from '../constants/htmlMediaEvents';
import debug from '../../debugger';
import logError from '../../loggers/logError';
import {
  LIVE_SEEK_LOAD_ERROR,
  LIVE_SEEK_PLAY_ERROR,
  LIVE_SEEK_SEEK_ERROR,
  LIVE_SEEK_SET_DURATION_ERROR,
} from '../hlsjs/constants/logging';
import playerName from '../constants/playerName';

const AVAILABLE_DURATION_HEADER = 'x-tunein-playlist-available-duration';
const DURATION_POLLING_INTERVAL = 5000;
const LIVE_HEAD_DELAY = 20;

/**
 * See https://tunein.atlassian.net/wiki/spaces/FNS/pages/232620284/Requesting+HLS+Streams+Playlist+Service+params
 * for details about these parameters and how they relate to LiveSeek implementation.
 * @param url {string}
 * @param time {number}
 * @returns {string}
 */
const liveSeekUrlBuilder = {
  start() {
    this.parts = [];
    return this;
  },
  posStart(time) {
    // Default to 1 for pos_start and pos_end since the streaming API doesn't
    // recognize 0 from the value not being set.
    this.parts.push(`pos_start=${time || 1}`);
    return this;
  },
  posEnd(time) {
    this.parts.push(`pos_end=${time || 1}`);
    return this;
  },
  segs(number) {
    this.parts.push(`segs=${number}`);
    return this;
  },
  finish(streamUrl) {
    const [url, search] = streamUrl.split('?');
    if (search) {
      this.parts = search.split('&').concat(this.parts);
    }
    return url + '?' + this.parts.join('&');
  },
};

export default class LiveSeekPlayer extends Player {
  constructor(hlsjsPlayer) {
    super(playerName.LiveSeekPlayer);
    this.hlsjsPlayer = hlsjsPlayer;
    this.hlsjsPlayer.inheritConfigs({ isLiveSeek: true });
    this.url = null;
    this.format = null;
    this.duration = null;
    this.durationPoller = null;
  }

  on(event, callback) {
    this.hlsjsPlayer.on(event, callback);
    this.addListener(event, callback);
  }

  off(event, callback) {
    this.hlsjsPlayer.off(event, callback);
    this.removeListener(event, callback);
  }

  /**
   * Load method that loads the given url into the hlsjsPlayer and starts
   * manually polling for the duration from the url and AVAILABLE_DURATION_HEADER.
   * @param url {string}
   * @param format {string}
   * @returns {Promise<void>}
   */
  async load(url, format, position, trackingUrl, isPreload) {
    this._stopDurationPolling();
    const liveSeekUrl = liveSeekUrlBuilder.start().segs(10000).finish(url);
    const loadPromise = this._load(
      liveSeekUrl,
      format,
      position,
      trackingUrl,
      isPreload,
    );
    const durationPromise = this._setDuration(url);
    await Promise.all([durationPromise, loadPromise]);
    this.url = url;
    this.format = format;

    if (!isPreload) {
      this._startDurationPolling();
    }
  }

  async play(playFromPositionPercent) {
    // Will need to eventually use the previously loaded fragment identifier here
    // instead of the playFromPositionPercent in order to get a more accurate playback
    // position. This will be added when we revisit liveSeek stream playback with smaller
    // HLS manifest files.
    if (playFromPositionPercent) {
      await this.seek(playFromPositionPercent);
    }

    if (!this.durationPoller) {
      this._startDurationPolling();
    }

    try {
      return this.hlsjsPlayer.play();
    } catch (ex) {
      logError(LIVE_SEEK_PLAY_ERROR, ex);
      throw ex;
    }
  }

  /**
   * A custom seek implementation that first unloads the currently set media player,
   * creates a liveSeekUrl based on the streaming API specifications (found here
   * https://tunein.atlassian.net/wiki/spaces/FNS/pages/232620284/Requesting+HLS+Streams+Playlist+Service+params),
   * and then loads the new liveSeekUrl into the media player and plays it. We are requesting
   * 10000 segments here in order to return an HLS manifest containing all of the segments
   * for the stream in order to properly reflect the media playback times. This will be optimized
   * in the future.
   *
   * @param percentage {number}
   * @returns {Promise<void>}
   */
  async seek(percentage) {
    try {
      await this.unload();
      const seekTime = percentageToTime(percentage, this.duration);
      const url = liveSeekUrlBuilder.start().segs(10000).finish(this.url);
      await this._load(url, this.format, seekTime);
      this.emit(htmlMediaEvents.seeked);
    } catch (ex) {
      debug.log('an error occurred while seeking', ex);
      this.emit(htmlMediaEvents.error, ex);
      logError(LIVE_SEEK_SEEK_ERROR, ex);
    }
  }

  pause() {
    return this.hlsjsPlayer.pause();
  }

  stop() {
    return this.hlsjsPlayer.stop();
  }

  unload() {
    return this.hlsjsPlayer.unload();
  }

  destroy() {
    this._stopDurationPolling();
    return this.hlsjsPlayer.destroy();
  }

  canChangePlaybackRate() {
    return this.hlsjsPlayer.canChangePlaybackRate();
  }

  getPlaybackRate() {
    return this.hlsjsPlayer.getPlaybackRate();
  }

  changePlaybackRate(rate) {
    this.hlsjsPlayer.changePlaybackRate(rate);
  }

  setVolume(volume) {
    this.hlsjsPlayer.setVolume(volume);
  }

  toggleMute() {
    this.hlsjsPlayer.toggleMute();
  }

  getVolume() {
    return this.hlsjsPlayer.getVolume();
  }

  setTitle(title) {
    this.hlsjsPlayer.setTitle(title);
  }

  getTimes() {
    try {
      const { elapsedSeconds } = this.hlsjsPlayer.getTimes() || {};
      if (!elapsedSeconds || !this.duration) {
        return {
          duration: 0,
          elapsedSeconds: 0,
          elapsedPercent: 0,
          isLiveSeekStream: true,
        };
      }
      const seconds =
        elapsedSeconds > this.duration ? this.duration : elapsedSeconds;
      const percentage = timeToPercentage(seconds, this.duration);
      return {
        duration: this.duration,
        elapsedSeconds,
        // The elapsed percent will never reach 100% when playing live seek streams. Seeking
        // to the live head, or 100%, actually seeks to a specified number of fragments
        // before the end of the stream. If the current time is within 20 seconds of the duration
        // of the media then assume that we are playing at the live head.
        elapsedPercent:
          seconds >= this.duration - LIVE_HEAD_DELAY ? 100 : percentage,
        isLiveSeekStream: true,
      };
    } catch (ex) {
      debug.log('could not get times for live seek player', ex);
      return {
        duration: 0,
        elapsedSeconds: 0,
        elapsedPercent: 0,
        isLiveSeekStream: true,
      };
    }
  }

  _load(url, format, position, trackingUrl, isPreload) {
    try {
      return this.hlsjsPlayer.load(
        url,
        format,
        position,
        trackingUrl,
        isPreload,
      );
    } catch (ex) {
      logError(LIVE_SEEK_LOAD_ERROR, ex);
      throw ex;
    }
  }

  _stopDurationPolling() {
    if (this.durationPoller) {
      clearInterval(this.durationPoller);
    }
  }

  async _setDuration(url) {
    try {
      const pollingUrl = liveSeekUrlBuilder.start().segs(1).finish(url);
      const response = await axios.get(pollingUrl);
      const duration = response.headers[AVAILABLE_DURATION_HEADER];
      if (!duration) {
        return;
      }
      const newDuration = parseInt(duration, 10);
      if (newDuration <= this.duration) {
        return;
      }
      this.duration = newDuration;
      this.emit(htmlMediaEvents.timeupdate, this.getTimes());
    } catch (ex) {
      logError(LIVE_SEEK_SET_DURATION_ERROR, ex);
      debug.log('an error occurred while polling for duration', ex);
    }
  }

  /**
   * Method to start polling for HLS live seek stream duration changes. Polling interval is
   * specified by DURATION_POLLING_INTERVAL constant.
   * @private
   */
  _startDurationPolling() {
    this.durationPoller = setInterval(
      () => this._setDuration(this.url),
      DURATION_POLLING_INTERVAL,
    );
  }
}
